import { Paths } from "@app/routes";
import { WithPermission } from "@atlas/composite";
import {
  Button,
  cn,
  Form,
  FormCommandSelectField,
  Tabs,
  TabsList,
  TabsTrigger,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { usePermission, useSessionData, useTranslation } from "@atlas/sdk";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  HUB_CATALOGS_KEY,
  useExportHubCatalogs,
  useHubCatalogTypes,
} from "@query";
import {
  DEFAULT_HUB_MANAGER_TAB,
  ExportTokenDialog,
  HUB_PERM,
  type HubManagerTabId,
  ImportTokenDialog,
  isHubManagerTabParam,
} from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";

import { HubManagerCatalogTypeFilter } from "./HubManagerCatalogTypeFilter";
import {
  areHubCatalogTypeFilterSelectionsEqual,
  filterHubManagerCatalogTypesBySelection,
  HUB_CATALOG_TYPE_FILTER_ALL,
  normalizeHubCatalogTypeFilterSelection,
  orderCatalogTypesSolutionFirst,
  shouldShowHubCatalogTypeFilterAll,
} from "./hubManagerCatalogFilters";
import { CatalogExplorer, MyCatalogs, Reviews } from "./tabs";
import { useHubManagerCatalogSections } from "./useHubManagerCatalogSections";

export function HubManagerPage() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([
    HUB_CATALOG_TYPE_FILTER_ALL,
  ]);
  const { userId } = useSessionData();
  const sessionUserId = userId ?? "";
  const [selectedForExport, setSelectedForExport] = useState<Set<string>>(
    new Set(),
  );
  const [exportedKey, setExportedKey] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { mutate: exportCatalogs, isPending: isExportPending } =
    useExportHubCatalogs();

  const handleExportToggle = useCallback(
    (catalogId: number, version: number) => {
      const key = `${catalogId}-${version}`;
      setSelectedForExport((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [],
  );

  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportSubmit = useCallback(() => {
    if (selectedForExport.size === 0) return;

    setIsExportDialogOpen(true);
    setExportError(null);

    const catalogsToExport = Array.from(selectedForExport).map((key) => {
      const [id, version] = key.split("-");
      return { catalogId: Number(id), version: Number(version) };
    });

    const payload = {
      catalogs: catalogsToExport,
    };

    exportCatalogs(payload, {
      onSuccess: (response) => {
        toast.add({
          title: t("hub.catalog_manager.export_success", {
            defaultValue: "Export successful",
          }),
          data: { type: "success" },
        });
        setSelectedForExport(new Set());
        if (response?.data) {
          setExportedKey(response.data);
        }
      },
      onError: (error: unknown) => {
        const apiError = error as { status?: number; message?: string };
        setExportError(
          apiError.message ||
            t("hub.catalog_manager.export_error", {
              defaultValue: "Export failed",
            }),
        );
        toast.add({
          title: t("hub.catalog_manager.export_error", {
            defaultValue: "Export failed",
          }),
          ...(apiError.status === 409 && apiError.message
            ? { description: apiError.message }
            : {}),
          data: { type: "error" },
        });
      },
    });
  }, [selectedForExport, toast, t, exportCatalogs]);

  const form = useForm({
    defaultValues: {
      searchQuery: "",
    },
  });

  useEffect(() => {
    const subscription = form.watch((value) => {
      setSearchQuery(value.searchQuery || "");
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  const tabParam = searchParams.get("tab");

  const { isPermission: hasCatalogCreate } = usePermission([
    HUB_PERM.CATALOG_CREATE,
  ]);
  const { isPermission: hasCatalogReviewParticipate } = usePermission([
    HUB_PERM.CATALOG_REVIEW_PARTICIPATE,
  ]);
  const { isPermission: hasCatalogReviewView } = usePermission([
    HUB_PERM.CATALOG_REVIEW_VIEW,
  ]);
  const hasReviewsTabAccess =
    hasCatalogReviewParticipate || hasCatalogReviewView;
  const hasMyCatalogsTabAccess = hasCatalogCreate;

  const activeTab = useMemo((): HubManagerTabId => {
    const fromParam = isHubManagerTabParam(tabParam)
      ? tabParam
      : DEFAULT_HUB_MANAGER_TAB;
    if (fromParam === "reviews" && !hasReviewsTabAccess) {
      return DEFAULT_HUB_MANAGER_TAB;
    }
    if (fromParam === "myCatalogs" && !hasMyCatalogsTabAccess) {
      return DEFAULT_HUB_MANAGER_TAB;
    }
    return fromParam;
  }, [tabParam, hasReviewsTabAccess, hasMyCatalogsTabAccess]);

  const isReviewsTab = activeTab === "reviews";

  const { data: catalogTypes = [] } = useHubCatalogTypes({
    mode: "DATA",
    sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
    startIndex: 0,
    limit: 100,
  });

  const catalogTypesOrdered = useMemo(
    () => orderCatalogTypesSolutionFirst(catalogTypes),
    [catalogTypes],
  );

  const {
    sections,
    sectionsPending,
    sectionsQueriesSettled,
    catalogTypesWithItems,
  } = useHubManagerCatalogSections({
    catalogTypes: catalogTypesOrdered,
    activeTab,
    searchQuery,
    userId: sessionUserId,
    enabled: !isReviewsTab,
    selectedTypeIds,
  });

  useEffect(() => {
    setSelectedTypeIds([HUB_CATALOG_TYPE_FILTER_ALL]);
  }, [activeTab]);

  useEffect(() => {
    if (!sectionsQueriesSettled) {
      return;
    }

    const visibleTypeIds = catalogTypesWithItems.map((type) =>
      String(type.typeId),
    );
    const showAllOption = shouldShowHubCatalogTypeFilterAll(
      visibleTypeIds.length,
    );

    setSelectedTypeIds((previous) => {
      const nextSelection = normalizeHubCatalogTypeFilterSelection(
        previous,
        visibleTypeIds,
        showAllOption,
      );
      return areHubCatalogTypeFilterSelectionsEqual(previous, nextSelection)
        ? previous
        : nextSelection;
    });
  }, [catalogTypesWithItems, sectionsQueriesSettled]);

  const selectedCatalogTypes = useMemo(
    () =>
      filterHubManagerCatalogTypesBySelection(
        catalogTypesWithItems,
        selectedTypeIds,
      ),
    [catalogTypesWithItems, selectedTypeIds],
  );

  const selectedTypeIdSet = useMemo(
    () => new Set(selectedCatalogTypes.map((type) => type.typeId)),
    [selectedCatalogTypes],
  );

  const selectedSections = useMemo(
    () =>
      sections.filter((section) =>
        selectedTypeIdSet.has(section.catalogType.typeId),
      ),
    [sections, selectedTypeIdSet],
  );

  const handleSelectedTypeIdsChange = useCallback((typeIds: string[]) => {
    setSelectedTypeIds(typeIds);
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="shrink-0 space-y-2">
        <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
          <Tabs
            value={activeTab}
            onValueChange={(v) =>
              setSearchParams({ tab: v }, { replace: true })
            }
            className="max-w-full overflow-x-auto">
            <TabsList variant="line" size="md" className="min-w-max">
              <TabsTrigger value="explorer">
                {t("hub.catalog_manager.explorer")}
              </TabsTrigger>
              {/* <TabsTrigger value="subscriptions">
              {t("hub.catalog_manager.subscriptions")}
            </TabsTrigger> */}
              <WithPermission permissions={[HUB_PERM.CATALOG_CREATE]}>
                <TabsTrigger value="myCatalogs">
                  {t("hub.catalog_manager.my_contributions", {
                    defaultValue: "My Catalogs",
                  })}
                </TabsTrigger>
              </WithPermission>
              <WithPermission permissions={[HUB_PERM.CATALOG_REVIEW_VIEW]}>
                <TabsTrigger value="reviews">
                  {t("hub.catalog_manager.reviews", { defaultValue: "Review" })}
                </TabsTrigger>
              </WithPermission>
            </TabsList>
          </Tabs>
          {!isReviewsTab && (
            <div className="flex items-center gap-2 sm:self-auto shrink-0">
              {activeTab === "myCatalogs" && (
                <>
                  <WithToolTip
                    tooltipText={
                      selectedForExport.size > 0
                        ? t("hub.catalog_manager.export_selected", {
                            defaultValue: "Export selected catalogs",
                          })
                        : t("hub.catalog_manager.export_disabled", {
                            defaultValue: "Select catalogs to enable export",
                          })
                    }>
                    <div className="inline-block">
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "shrink-0",
                          selectedForExport.size > 0
                            ? "gap-1.5 px-3"
                            : "w-10 px-0",
                        )}
                        onClick={handleExportSubmit}
                        disabled={
                          isExportPending || selectedForExport.size === 0
                        }>
                        <Upload className="size-4" />
                        {selectedForExport.size > 0 &&
                          `(${selectedForExport.size})`}
                      </Button>
                    </div>
                  </WithToolTip>
                  <WithToolTip
                    tooltipText={t("hub.catalog_manager.import_catalogs", {
                      defaultValue: "Import catalogs",
                    })}>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setImportDialogOpen(true)}>
                      <Download className="size-4" />
                    </Button>
                  </WithToolTip>
                </>
              )}
              <WithPermission permissions={[HUB_PERM.CATALOG_CREATE]}>
                <Button size="md" className="shrink-0 gap-1.5" asChild>
                  <Link to={`/${Paths.CatalogManager}/${Paths.CreateCatalog}`}>
                    <Plus className="size-4" />
                    {t("hub.catalog_manager.breadcrumb_contribute")}
                  </Link>
                </Button>
              </WithPermission>
            </div>
          )}
        </div>
        {!isReviewsTab && (
          <div className="flex items-center gap-3 p-2">
            <div className="min-w-0 flex-1">
              <HubManagerCatalogTypeFilter
                catalogTypes={catalogTypesWithItems}
                selectedTypeIds={selectedTypeIds}
                onSelectedTypeIdsChange={handleSelectedTypeIdsChange}
              />
            </div>
            <Form {...form}>
              <form className="shrink-0">
                <FormCommandSelectField
                  control={form.control}
                  name="searchQuery"
                  variant="sm"
                  displayType="search"
                  placeholder={t("hub.catalog_manager.search_placeholder")}
                  debounceMs={300}
                  className="min-w-35 max-w-sm"
                />
              </form>
            </Form>
          </div>
        )}
      </div>

      {isReviewsTab ? (
        <Reviews />
      ) : activeTab === "explorer" ? (
        <CatalogExplorer
          sections={selectedSections}
          sectionsLoading={sectionsPending}
          selectedTypeIds={selectedTypeIds}
        />
      ) : (
        /* activeTab === "subscriptions" ? (
        <SubscriptionsTab
          searchQuery={searchQuery}
          catalogTypes={filteredCatalogTypes}
        />
      ) : */ <MyCatalogs
          sections={selectedSections}
          sectionsLoading={sectionsPending}
          selectedForExport={selectedForExport}
          onExportToggle={handleExportToggle}
        />
      )}

      <ExportTokenDialog
        exportedKey={exportedKey}
        isLoading={isExportPending}
        onClose={() => {
          setExportedKey(null);
          setIsExportDialogOpen(false);
          setExportError(null);
        }}
        open={isExportDialogOpen}
        error={exportError}
        title={t("hub.catalog_manager.export_key_title", {
          defaultValue: "Export Catalogs Successful",
        })}
        description={t("hub.catalog_manager.export_key_description", {
          defaultValue: "Please copy token key to import your catalogs.",
        })}
      />

      <ImportTokenDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
        }}
        title={t("hub.catalog_manager.import_title", {
          defaultValue: "Import Catalogs",
        })}
        description={t("hub.catalog_manager.import_description", {
          defaultValue: "Paste the token key to import catalogs.",
        })}
      />
    </div>
  );
}
